import React, { useState, useCallback } from 'react';
import { Package, AlertCircle, Info, Plus, Trash2 } from 'lucide-react';
import type {
  WebhookColumnMappingInput,
  FieldDataType,
  TransformConfig,
} from '../../services/integrations-api';

// ---------------------------------------------------------------------------
// Constants (same column groups as FieldMappingEditor but for detail-level slots)
// ---------------------------------------------------------------------------

const DETAIL_COLUMN_GROUPS = [
  { label: 'Measures', slots: Array.from({ length: 6 }, (_, i) => `measure_${i + 1}`) },
  { label: 'Names', slots: Array.from({ length: 3 }, (_, i) => `name_${i + 1}`) },
  { label: 'Labels', slots: Array.from({ length: 4 }, (_, i) => `label_${i + 1}`) },
  { label: 'References', slots: Array.from({ length: 5 }, (_, i) => `ref_${i + 1}`) },
  { label: 'Notes', slots: ['note_1'] },
  { label: 'Flags', slots: ['flag_1', 'flag_2'] },
];

const DATA_TYPES: { value: FieldDataType; label: string }[] = [
  { value: 'currency', label: 'Currency' },
  { value: 'count', label: 'Count' },
  { value: 'percentage', label: 'Percentage' },
  { value: 'name', label: 'Name' },
  { value: 'category', label: 'Category' },
  { value: 'reference', label: 'Reference' },
  { value: 'identifier', label: 'Identifier' },
  { value: 'text', label: 'Text' },
  { value: 'boolean', label: 'Boolean' },
  { value: 'date', label: 'Date' },
];

const TRANSFORM_OPTIONS = [
  { value: 'none', label: 'No transformation' },
  { value: 'parse_number', label: 'Parse as number' },
  { value: 'divide', label: 'Divide by 100' },
  { value: 'uppercase', label: 'UPPERCASE' },
  { value: 'lowercase', label: 'lowercase' },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function slugify(text: string): string {
  return text.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
}

function emptyDetailMapping(): WebhookColumnMappingInput {
  return {
    dbColumn: '',
    sourceApiField: '',
    friendlySlug: '',
    friendlyLabel: '',
    description: '',
    dataType: 'text',
  };
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface DetectedArray {
  path: string;
  itemCount: number;
  sampleItemKeys: string[];
  sampleItem: unknown;
}

interface DetailsMappingSectionProps {
  detectedArrays: DetectedArray[];
  detailMappings: WebhookColumnMappingInput[];
  detailsArrayPath: string;
  detailsEnabled: boolean;
  onToggle: (enabled: boolean) => void;
  onArrayPathChange: (path: string) => void;
  onDetailMappingsChange: (mappings: WebhookColumnMappingInput[]) => void;
  isLoadingArrays?: boolean;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const DetailsMappingSection: React.FC<DetailsMappingSectionProps> = ({
  detectedArrays,
  detailMappings,
  detailsArrayPath,
  detailsEnabled,
  onToggle,
  onArrayPathChange,
  onDetailMappingsChange,
  isLoadingArrays = false,
}) => {
  const [_showAdvanced, _setShowAdvanced] = useState(false);

  const selectedArray = detectedArrays.find(a => a.path === detailsArrayPath);

  const getItemPaths = useCallback((obj: unknown, prefix = ''): string[] => {
    const paths: string[] = [];
    if (!obj || typeof obj !== 'object') return paths;
    const record = obj as Record<string, unknown>;
    for (const key in record) {
      const p = prefix ? `${prefix}.${key}` : key;
      const v = record[key];
      if (v && typeof v === 'object' && !Array.isArray(v)) {
        paths.push(...getItemPaths(v, p));
      } else {
        paths.push(p);
      }
    }
    return paths;
  }, []);

  const availableItemPaths = selectedArray?.sampleItem ? getItemPaths(selectedArray.sampleItem) : [];
  const usedDetailCols = new Set(detailMappings.map(m => m.dbColumn).filter(Boolean));

  const updateDetailMapping = (idx: number, patch: Partial<WebhookColumnMappingInput>) => {
    const updated = detailMappings.map((m, i) => {
      if (i !== idx) return m;
      const u = { ...m, ...patch };
      if (patch.friendlyLabel !== undefined && !m.friendlySlug) {
        u.friendlySlug = slugify(patch.friendlyLabel);
      }
      return u;
    });
    onDetailMappingsChange(updated);
  };

  const addDetailMapping = () => onDetailMappingsChange([...detailMappings, emptyDetailMapping()]);
  const removeDetailMapping = (idx: number) => onDetailMappingsChange(detailMappings.filter((_, i) => i !== idx));

  const handleArrayPathChange = (path: string) => {
    onArrayPathChange(path);
    onDetailMappingsChange([emptyDetailMapping()]);
  };

  // ---------------------------------------------------------------------------
  // Render a single detail mapping row
  // ---------------------------------------------------------------------------

  const renderDetailRow = (m: WebhookColumnMappingInput, idx: number) => {
    const isLiteral = m.sourceApiField.startsWith('LITERAL:');

    return (
      <div
        key={idx}
        className="border border-border rounded-lg p-3 bg-card space-y-2"
      >
        <div className="flex items-center justify-between gap-2">
          <div className="flex-1">
            <label className="block text-xs font-medium text-muted-foreground mb-1">Column Slot</label>
            <select
              className="w-full border border-input p-2 rounded text-sm bg-card text-foreground focus:ring-2 focus:ring-ring"
              value={m.dbColumn}
              onChange={e => updateDetailMapping(idx, { dbColumn: e.target.value })}
            >
              <option value="">Select column...</option>
              {DETAIL_COLUMN_GROUPS.map(g => (
                <optgroup key={g.label} label={g.label}>
                  {g.slots.map(s => (
                    <option key={s} value={s} disabled={usedDetailCols.has(s) && m.dbColumn !== s}>
                      {s}{usedDetailCols.has(s) && m.dbColumn !== s ? ' (used)' : ''}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
          </div>
          <button
            onClick={() => removeDetailMapping(idx)}
            className="mt-5 p-2 text-destructive hover:text-destructive/80"
            title="Remove"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>

        {/* Source path or literal */}
        <div>
          <div className="flex items-center gap-3 mb-1">
            <label className="flex items-center cursor-pointer text-xs text-muted-foreground">
              <input
                type="checkbox"
                checked={isLiteral}
                onChange={e => updateDetailMapping(idx, { sourceApiField: e.target.checked ? 'LITERAL:' : '' })}
                className="mr-1 rounded border-input text-primary focus:ring-ring"
              />
              Literal
            </label>
          </div>
          {isLiteral ? (
            <input
              type="text"
              className="w-full border border-input p-2 rounded text-sm bg-card text-foreground focus:ring-2 focus:ring-ring"
              value={m.sourceApiField.replace('LITERAL:', '')}
              onChange={e => updateDetailMapping(idx, { sourceApiField: `LITERAL:${e.target.value}` })}
              placeholder="Enter literal value"
            />
          ) : (
            <select
              className="w-full border border-input p-2 rounded text-sm bg-card text-foreground focus:ring-2 focus:ring-ring"
              value={m.sourceApiField}
              onChange={e => updateDetailMapping(idx, { sourceApiField: e.target.value })}
              disabled={!detailsArrayPath}
            >
              <option value="">Select field...</option>
              {availableItemPaths.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          )}
        </div>

        {/* Transform */}
        {!isLiteral && m.sourceApiField && (
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1">Transform</label>
            <select
              className="w-full border border-input p-1.5 rounded text-xs bg-card text-foreground"
              value={(m.transformConfig as TransformConfig)?.type || 'none'}
              onChange={e => {
                const v = e.target.value;
                if (v === 'none') {
                  updateDetailMapping(idx, { transformConfig: undefined });
                } else {
                  const cfg: TransformConfig = { type: v as TransformConfig['type'] };
                  if (v === 'divide') cfg.divisor = 100;
                  updateDetailMapping(idx, { transformConfig: cfg });
                }
              }}
            >
              {TRANSFORM_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
        )}

        {/* Metadata */}
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1">
              Label <span className="text-destructive">*</span>
            </label>
            <input
              type="text"
              className="w-full border border-input p-1.5 rounded text-xs bg-card text-foreground focus:ring-2 focus:ring-ring"
              value={m.friendlyLabel}
              onChange={e => {
                const label = e.target.value;
                const patch: Partial<WebhookColumnMappingInput> = { friendlyLabel: label };
                if (!m.friendlySlug || m.friendlySlug === slugify(m.friendlyLabel)) {
                  patch.friendlySlug = slugify(label);
                }
                updateDetailMapping(idx, patch);
              }}
              placeholder="e.g., Item Name"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1">Slug</label>
            <input
              type="text"
              className="w-full border border-input p-1.5 rounded text-xs bg-card text-foreground focus:ring-2 focus:ring-ring"
              value={m.friendlySlug}
              onChange={e => updateDetailMapping(idx, { friendlySlug: e.target.value })}
              placeholder="e.g., item_name"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1">Data Type</label>
            <select
              className="w-full border border-input p-1.5 rounded text-xs bg-card text-foreground"
              value={m.dataType}
              onChange={e => updateDetailMapping(idx, { dataType: e.target.value as FieldDataType })}
            >
              {DATA_TYPES.map(dt => <option key={dt.value} value={dt.value}>{dt.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1">Unit</label>
            <input
              type="text"
              className="w-full border border-input p-1.5 rounded text-xs bg-card text-foreground"
              value={m.unit || ''}
              onChange={e => updateDetailMapping(idx, { unit: e.target.value || undefined })}
              placeholder="optional"
            />
          </div>
        </div>

        <div>
          <label className="block text-xs font-medium text-muted-foreground mb-1">Description</label>
          <input
            type="text"
            className="w-full border border-input p-1.5 rounded text-xs bg-card text-foreground"
            value={m.description}
            onChange={e => updateDetailMapping(idx, { description: e.target.value })}
            placeholder="optional"
          />
        </div>
      </div>
    );
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div className="border border-border rounded-lg overflow-hidden">
      {/* Header toggle */}
      <div
        className={`flex items-center justify-between p-4 cursor-pointer transition-colors ${
          detailsEnabled
            ? 'bg-accent border-b border-primary/20'
            : 'bg-muted/50 hover:bg-muted'
        }`}
        onClick={() => onToggle(!detailsEnabled)}
      >
        <div className="flex items-center space-x-3">
          <div className={`p-2 rounded-lg ${detailsEnabled ? 'bg-accent' : 'bg-muted'}`}>
            <Package className={`w-5 h-5 ${detailsEnabled ? 'text-primary' : 'text-muted-foreground'}`} />
          </div>
          <div>
            <h4 className="font-semibold text-foreground">Line Items / Details</h4>
            <p className="text-sm text-muted-foreground">
              {detailsEnabled ? 'Extract individual items from this webhook' : 'Enable to extract line items from arrays'}
            </p>
          </div>
        </div>

        <div className="flex items-center space-x-3">
          {detectedArrays.length > 0 && !detailsEnabled && (
            <span className="text-xs bg-info/15 text-info px-2 py-1 rounded-full">
              {detectedArrays.length} array{detectedArrays.length > 1 ? 's' : ''} detected
            </span>
          )}
          <div className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
            detailsEnabled ? 'bg-primary' : 'bg-muted'
          }`}>
            <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
              detailsEnabled ? 'translate-x-6' : 'translate-x-1'
            }`} />
          </div>
        </div>
      </div>

      {/* Content */}
      {detailsEnabled && (
        <div className="p-4 space-y-4 bg-card">
          {/* Array selection */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Select Array to Extract Items From <span className="text-destructive">*</span>
            </label>
            {isLoadingArrays ? (
              <p className="text-sm text-muted-foreground py-2">Detecting arrays...</p>
            ) : detectedArrays.length === 0 ? (
              <div className="bg-warn/15 border border-warn/20 rounded-lg p-3 flex items-start space-x-2">
                <AlertCircle className="w-5 h-5 text-warn mt-0.5" />
                <p className="text-sm text-warn">
                  No arrays detected in the sample payload.
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {detectedArrays.map(arr => (
                  <label
                    key={arr.path}
                    className={`flex items-start space-x-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                      detailsArrayPath === arr.path
                        ? 'border-primary bg-accent'
                        : 'border-border hover:bg-muted'
                    }`}
                  >
                    <input
                      type="radio"
                      name="detailArrayPath"
                      value={arr.path}
                      checked={detailsArrayPath === arr.path}
                      onChange={() => handleArrayPathChange(arr.path)}
                      className="mt-1"
                    />
                    <div className="flex-1">
                      <span className="font-mono text-sm font-medium text-foreground">{arr.path}</span>
                      <div className="text-xs text-muted-foreground mt-1">
                        {arr.itemCount} items &bull; Fields: {arr.sampleItemKeys.slice(0, 5).join(', ')}
                        {arr.sampleItemKeys.length > 5 && ` +${arr.sampleItemKeys.length - 5} more`}
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            )}
          </div>

          {/* Detail field mappings */}
          {detailsArrayPath && (
            <>
              <div className="border-t border-border pt-4">
                <div className="flex items-center justify-between mb-3">
                  <h5 className="text-sm font-semibold text-foreground">
                    Map Detail Fields
                  </h5>
                  <button
                    onClick={addDetailMapping}
                    className="flex items-center text-sm text-primary hover:text-primary/80"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Add Field
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {detailMappings.map((m, i) => renderDetailRow(m, i))}
                </div>
              </div>

              <div className="bg-info/15 border border-info/20 rounded-lg p-3 flex items-start space-x-2">
                <Info className="w-4 h-4 text-info mt-0.5" />
                <p className="text-sm text-info">
                  <strong>How it works:</strong> Each item in the selected array is saved as a
                  separate transaction detail record, mapped to the column slots you choose.
                </p>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default DetailsMappingSection;
